<template>
    <div>
        <v-toolbar v-if="showOrgPools || showSpacePools" flat color="#f1f2f3">
            <v-container fluid>
                <v-row>
                    <v-col cols="12" sm="6" class="d-flex align-center">
                        <v-autocomplete
                            v-if="showOrgPools"
                            outlined
                            :items="$isError(orgPools) ? [] : orgPools"
                            :loading="fetching.orgPools || fetchingAllCharts"
                            :disabled="fetching.orgPools || fetchingAllCharts"
                            :error="$isError(orgPools)"
                            :error-messages="getErrorMsg(orgPools)"
                            v-model="selectedOrgId"
                            item-text="long_id"
                            item-value="oid"
                            dense
                            label="Organization"
                            placeholder="Type to search"
                            hide-details="auto"
                            clearable
                            @click:clear="clearOrg()"
                            :menu-props="{ closeOnContentClick: true }">
                            <template v-slot:item="data">
                                <v-list-item :to="selectLink('oid', { rpid: $route.params.rpid, oid: data.item.oid })">
                                    <v-list-item-content :class="data.item.deleted && 'deleted'">
                                        {{ data.item.long_id }}
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                        <contacts-menu v-if="showOrgPools" level="org" :rpId="Number($route.params.rpid)" :orgId="Number($route.params.oid)"></contacts-menu>
                        <div class="pl-6" :class="{ hidden: !showSpacePools }">
                            <v-icon>keyboard_arrow_right</v-icon>
                        </div>
                    </v-col>
                    <v-col cols="12" sm="6" class="d-flex align-center justify-end">
                        <v-autocomplete
                            v-if="showSpacePools"
                            outlined
                            :items="$isError(spacePools) ? [] : spacePools"
                            :loading="fetching.orgPools || fetching.spacePools || fetchingAllCharts"
                            :disabled="fetching.orgPools || fetching.spacePools || fetchingAllCharts"
                            :error="$isError(spacePools)"
                            :error-messages="getErrorMsg(spacePools)"
                            v-model="selectedSpaceId"
                            item-text="long_id"
                            item-value="sid"
                            dense
                            label="Space"
                            placeholder="Type to search"
                            hide-details="auto"
                            clearable
                            @click:clear="clearSpace()"
                            :menu-props="{ closeOnContentClick: true }">
                            <template v-slot:item="data">
                                <v-list-item :to="selectLink('sid', { rpid: $route.params.rpid, oid: $route.params.oid, sid: data.item.sid })">
                                    <v-list-item-content :class="data.item.deleted && 'deleted'">
                                        {{ data.item.long_id }}
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                        <contacts-menu
                            v-if="showSpacePools"
                            level="space"
                            :rpId="Number($route.params.rpid)"
                            :spaceId="Number($route.params.sid)"></contacts-menu>
                    </v-col>
                </v-row>
            </v-container>
        </v-toolbar>

        <div>
            <router-view @fetching-charts="fetchingCharts"></router-view>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

const ContactsMenu = () => import('../components/ContactsMenu.vue')

export default {
    name: 'MonitoringDashboard',
    components: { ContactsMenu },
    data() {
        return {
            orgPools: [],
            spacePools: [],
            fetching: {
                orgPools: true,
                spacePools: true
            },
            selectedOrgId: null,
            selectedSpaceId: null,
            errorMessage: 'Failed to fetch resource list!',
            fetchingAllCharts: false
        }
    },
    methods: {
        async getOrgList() {
            if (!this.$route.params.rpid) return
            this.fetching.orgPools = true
            const { data } = await this.$axios.get(`/resource/rp_tree/rpid/${this.$route.params.rpid}`)
            this.orgPools = data
            this.fetching.orgPools = false
        },
        async getSpaceList() {
            if (!this.$route.params.rpid || !this.$route.params.oid) return
            this.fetching.spacePools = true
            const { data } = await this.$axios.get(`/resource/rp_tree/rpid/${this.$route.params.rpid}/oid/${this.$route.params.oid}`)
            this.spacePools = data
            this.fetching.spacePools = false
        },
        getErrorMsg(target) {
            return this.$isError(target) ? this.errorMessage : ''
        },
        selectLink(level, params) {
            params = { ...params, startDate: this.$route.params.startDate, endDate: this.$route.params.endDate }
            return { name: `reporting-${level}-${this.currentTab}`, params }
        },
        clearOrg() {
            this.$router.push({
                name: `reporting-rp-${this.currentTab}`,
                params: { rpid: this.$route.params.rpid, startDate: this.$route.params.startDate, endDate: this.$route.params.endDate }
            })
        },
        clearSpace() {
            this.$router.push({
                name: `reporting-rp-${this.currentTab}`,
                params: {
                    rpid: this.$route.params.rpid,
                    oid: this.$route.params.oid,
                    startDate: this.$route.params.startDate,
                    endDate: this.$route.params.endDate
                }
            })
        },
        fetchingCharts(fetching) {
            this.fetchingAllCharts = fetching
        }
    },
    computed: {
        ...mapState('userStore', ['isAccountManager', 'availableResourcePools', 'selectedResourcePool']),
        ...mapGetters(['isSysadmin']),
        initialResourcePoolId() {
            if (Number.isFinite(Number(this.$route.params.rpid))) {
                return Number(this.$route.params.rpid)
            }

            if (Number.isFinite(Number(this.selectedResourcePool))) {
                return Number(this.selectedResourcePool)
            }

            if (this.availableResourcePools.length > 0) {
                return Number(this.availableResourcePools[0].rpid)
            }

            return null
        },
        isResourcePoolOnlyRoute() {
            return ['resource-sizes', 'integrations', 'node-allocation'].some(route => (this.$route.name || '').includes(route))
        },
        showOrgPools() {
            return !this.$route.name.includes('overview') && !this.isResourcePoolOnlyRoute
        },
        showSpacePools() {
            return this.selectedOrgId && !this.$route.name.includes('overview') && !this.$route.name.includes('inventory') && !this.isResourcePoolOnlyRoute
        },
        currentTab() {
            const tabName = this.$route.name.split('-').slice(2).join('-')
            return tabName || 'overview'
        }
    },
    watch: {
        $route: {
            handler(to) {
                if (to.name === 'reporting-dashboard' && this.initialResourcePoolId !== null) {
                    this.$router.push({ name: 'reporting-rp-overview', params: { rpid: this.initialResourcePoolId } })
                    return
                }

                this.getOrgList()
                this.getSpaceList()

                if (to.params.oid) {
                    this.selectedOrgId = Number(to.params.oid)
                } else {
                    this.selectedOrgId = null
                }

                if (to.params.sid) {
                    this.selectedSpaceId = Number(to.params.sid)
                } else {
                    this.selectedSpaceId = null
                }
            },
            immediate: true
        },
        availableResourcePools() {
            if (this.$route.name === 'reporting-dashboard' && this.initialResourcePoolId !== null) {
                this.$router.push({ name: 'reporting-rp-overview', params: { rpid: this.initialResourcePoolId } })
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.hidden {
    visibility: hidden;
}

.deleted {
    text-decoration: line-through;
    opacity: 0.5;
}
</style>
